import { Minus, Plus, ShoppingCartSimple } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { useStore } from '../../context/StoreContext';

/**
 * Compact add-to-cart: cart icon that expands to +/- counter after first add.
 * @param {Object} props
 * @param {Object} props.product - Product/hit object with objectID
 * @param {string|null} props.queryID - Algolia queryID for attribution
 * @param {boolean} props.compact - If true, use compact cart-icon style; if false, use PDP prominent style
 * @param {boolean} props.compactShowLabel - If true (with compact), show icon + short “add to cart” label
 * @param {function} props.onAdd - Optional callback when item added (e.g. open cart)
 */
export const AddToCartStepper = ({ product, queryID = null, compact = true, compactShowLabel = false, onAdd }) => {
  const { t } = useTranslation();
  const { cartItems, addToCart, updateQuantity } = useCart();
  const { selectedStore } = useStore();

  const cartItem = cartItems.find(item => item.objectID === product.objectID);
  const quantity = cartItem?.quantity ?? 0;

  const handleAdd = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const productWithStore = { ...product, selectedStore: selectedStore || '1' };
    addToCart(productWithStore, 1, queryID);
    onAdd?.();
  };

  const handleIncrement = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    updateQuantity(product.objectID, quantity + 1);
  };

  const handleDecrement = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (quantity <= 1) {
      updateQuantity(product.objectID, 0); // removes via CartContext
    } else {
      updateQuantity(product.objectID, quantity - 1);
    }
  };

  const baseBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#4B164C',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
  };

  if (compact) {
    // Compact: cart icon or [-][qty][+]
    const size = 28;
    const iconSize = 14;
    if (quantity === 0) {
      if (compactShowLabel) {
        return (
          <button
            type="button"
            onClick={handleAdd}
            aria-label={t('add_to_cart')}
            style={{
              ...baseBtn,
              minHeight: 36,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              gap: 8,
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#6d2c7a'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#4B164C'; }}
          >
            <ShoppingCartSimple size={16} weight="bold" aria-hidden />
            {t('add_to_cart_short')}
          </button>
        );
      }
      return (
        <button
          type="button"
          onClick={handleAdd}
          aria-label={t('add_to_cart')}
          style={{
            ...baseBtn,
            width: size,
            height: size,
            borderRadius: 8,
            minWidth: size,
            flexShrink: 0,
          }}
          onMouseOver={e => { e.currentTarget.style.background = '#6d2c7a'; }}
          onMouseOut={e => { e.currentTarget.style.background = '#4B164C'; }}
        >
          <ShoppingCartSimple size={iconSize} weight="bold" />
        </button>
      );
    }
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0,
          background: '#4B164C',
          borderRadius: 8,
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleDecrement}
          style={{
            ...baseBtn,
            width: size,
            height: size,
            minWidth: size,
          }}
          onMouseOver={e => { e.currentTarget.style.background = '#6d2c7a'; }}
          onMouseOut={e => { e.currentTarget.style.background = '#4B164C'; }}
        >
          <Minus size={12} weight="bold" />
        </button>
        <span style={{
          minWidth: 24,
          textAlign: 'center',
          fontSize: 13,
          fontWeight: 600,
          color: '#fff',
        }}>
          {quantity}
        </span>
        <button
          type="button"
          onClick={handleIncrement}
          style={{
            ...baseBtn,
            width: size,
            height: size,
            minWidth: size,
          }}
          onMouseOver={e => { e.currentTarget.style.background = '#6d2c7a'; }}
          onMouseOut={e => { e.currentTarget.style.background = '#4B164C'; }}
        >
          <Plus size={12} weight="bold" />
        </button>
      </div>
    );
  }

  // PDP: prominent "Add to cart" or stepper
  if (quantity === 0) {
    return (
      <button
        type="button"
        onClick={handleAdd}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          background: '#4B164C',
          color: '#fff',
          border: 'none',
          borderRadius: 12,
          padding: '14px 28px',
          fontWeight: 600,
          fontSize: 16,
          cursor: 'pointer',
          transition: 'background 0.2s',
          width: '100%',
          minWidth: 200,
          maxWidth: 320,
          justifyContent: 'center',
        }}
        onMouseOver={e => { e.currentTarget.style.background = '#6d2c7a'; }}
        onMouseOut={e => { e.currentTarget.style.background = '#4B164C'; }}
      >
        <ShoppingCartSimple size={22} weight="bold" />
        Ajouter au panier
      </button>
    );
  }
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0,
        background: '#4B164C',
        borderRadius: 12,
        overflow: 'hidden',
        maxWidth: 200,
      }}
      onClick={e => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={handleDecrement}
        style={{
          ...baseBtn,
          width: 44,
          height: 44,
          padding: 0,
        }}
        onMouseOver={e => { e.currentTarget.style.background = '#6d2c7a'; }}
        onMouseOut={e => { e.currentTarget.style.background = '#4B164C'; }}
      >
        <Minus size={18} weight="bold" />
      </button>
      <span style={{
        minWidth: 48,
        textAlign: 'center',
        fontSize: 16,
        fontWeight: 600,
        color: '#fff',
      }}>
        {quantity}
      </span>
      <button
        type="button"
        onClick={handleIncrement}
        style={{
          ...baseBtn,
          width: 44,
          height: 44,
          padding: 0,
        }}
        onMouseOver={e => { e.currentTarget.style.background = '#6d2c7a'; }}
        onMouseOut={e => { e.currentTarget.style.background = '#4B164C'; }}
      >
        <Plus size={18} weight="bold" />
      </button>
    </div>
  );
};
